import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContexte';
import { useAccess } from '../../contexts/AccessContext';
import './ZonesPage.css';

let nextKey = 1;

// Ligne éditable : `originalZone` = nom au chargement (null pour une zone ajoutée), sert à suivre les renommages
const toRows = (zones) => zones.map(z => ({ key: nextKey++, zone: z.zone, donjon: z.donjon || '', bda: !!z.bda, originalZone: z.zone }));

const normalize = (name) => name.trim().toLowerCase();

// Copie d'une liste de zones : noms, donjons et ordre de la source ; statut BDA et suivi des renommages
// conservés depuis la liste courante (correspondance par nom, sinon par donjon unique des deux côtés)
export const copyZoneList = (current, sourceZones) => {
    const used = new Set();
    const byName = new Map(current.map(r => [normalize(r.zone), r]));
    // Donjon unique des deux côtés : permet de suivre une zone renommée sur la source
    const uniqueBy = (list, get) => {
        const counts = {};
        list.forEach(x => { const k = normalize(get(x) || ''); if (k) counts[k] = (counts[k] || 0) + 1; });
        return k => k && counts[k] === 1;
    };
    const uniqueHere = uniqueBy(current, r => r.donjon);
    const uniqueThere = uniqueBy(sourceZones, z => z.donjon);
    const byDonjon = new Map(current.filter(r => uniqueHere(normalize(r.donjon))).map(r => [normalize(r.donjon), r]));

    const matches = sourceZones.map(z => {
        let match = byName.get(normalize(z.zone));
        if (match && used.has(match.key)) match = null;
        if (match) used.add(match.key);
        return match;
    });
    const next = sourceZones.map((z, i) => {
        let match = matches[i];
        const donjonKey = normalize(z.donjon || '');
        if (!match && uniqueThere(donjonKey)) {
            const candidate = byDonjon.get(donjonKey);
            if (candidate && !used.has(candidate.key)) { match = candidate; used.add(candidate.key); }
        }
        return {
            key: match?.key ?? nextKey++,
            zone: z.zone,
            donjon: z.donjon || '',
            bda: match ? match.bda : false,
            originalZone: match ? match.originalZone : null,
        };
    });
    // Zones BDA de ce serveur absentes de la source : conservées en fin de liste
    return [...next, ...current.filter(r => r.bda && !used.has(r.key))];
};

const ZonesPage = () => {
    const { serverId } = useParams();
    const { success, error: showError } = useNotification();
    const { t } = useLanguage();
    const { user } = useAuth();
    const { can } = useAccess();

    const [rows, setRows] = useState([]);
    const [saved, setSaved] = useState([]);   // dernière version enregistrée (pour détecter les modifications)
    const [custom, setCustom] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [view, setView] = useState('all');   // all | open | bda
    const [newZone, setNewZone] = useState({ zone: '', donjon: '' });

    const applyResponse = useCallback((data) => {
        const zones = Array.isArray(data?.zones) ? data.zones : [];
        setRows(toRows(zones));
        setSaved(zones.map(z => ({ zone: z.zone, donjon: z.donjon || '', bda: !!z.bda })));
        setCustom(!!data?.custom);
        setUpdatedAt(data?.updatedAt || null);
    }, []);

    useEffect(() => {
        if (!serverId) return;
        setLoading(true);
        api.get(`/zones/${serverId}`)
            .then(res => applyResponse(res.data))
            .catch(() => showError(t('zonesAdmin.loadError', 'Erreur lors du chargement des zones')))
            .finally(() => setLoading(false));
    }, [serverId, applyResponse, showError, t]);

    // ── Contrôles ──────────────────────────────────────────────────────
    const duplicates = useMemo(() => {
        const counts = {};
        rows.forEach(r => { const k = normalize(r.zone); if (k) counts[k] = (counts[k] || 0) + 1; });
        return new Set(Object.keys(counts).filter(k => counts[k] > 1));
    }, [rows]);

    const emptyCount = rows.filter(r => !r.zone.trim()).length;
    const removedCount = saved.filter(s => !rows.some(r => r.originalZone === s.zone)).length;
    const isDirty = rows.length !== saved.length
        || rows.some((r, i) => r.zone.trim() !== saved[i].zone || r.donjon.trim() !== saved[i].donjon || r.bda !== saved[i].bda);
    const canSave = isDirty && rows.length > 0 && emptyCount === 0 && duplicates.size === 0 && !saving;

    // ── Édition ────────────────────────────────────────────────────────
    const updateRow = (key, field, value) =>
        setRows(prev => prev.map(r => (r.key === key ? { ...r, [field]: value } : r)));

    const removeRow = (key) => setRows(prev => prev.filter(r => r.key !== key));

    const moveRow = (key, delta) => setRows(prev => {
        const idx = prev.findIndex(r => r.key === key);
        const target = idx + delta;
        if (idx < 0 || target < 0 || target >= prev.length) return prev;
        const next = [...prev];
        [next[idx], next[target]] = [next[target], next[idx]];
        return next;
    });

    const addZone = (e) => {
        e.preventDefault();
        const zone = newZone.zone.trim();
        if (!zone) return;
        if (rows.some(r => normalize(r.zone) === normalize(zone))) {
            showError(t('zonesAdmin.alreadyExists', 'La zone « {zone} » existe déjà', { zone }));
            return;
        }
        setRows(prev => [...prev, { key: nextKey++, zone, donjon: newZone.donjon.trim(), bda: false, originalZone: null }]);
        setNewZone({ zone: '', donjon: '' });
        setSearch('');
    };

    // ── Enregistrement ─────────────────────────────────────────────────
    const save = async () => {
        if (!canSave) return;
        if (removedCount > 0 && !window.confirm(
            t('zonesAdmin.confirmRemoved', '{n} zone(s) supprimée(s) seront aussi retirées des préférences des membres.\n\nEnregistrer la liste ?', { n: removedCount })
        )) return;

        setSaving(true);
        try {
            const res = await api.put(`/zones/${serverId}`, {
                zones: rows.map(r => ({ zone: r.zone.trim(), donjon: r.donjon.trim(), bda: r.bda, originalZone: r.originalZone }))
            });
            applyResponse(res.data);
            const prefsNote = res.data?.updatedPrefs ? ` · ${t('zonesAdmin.prefsUpdated', '{n} liste(s) de préférences mise(s) à jour', { n: res.data.updatedPrefs })}` : '';
            success(`${t('zonesAdmin.saved', 'Zones enregistrées')}${prefsNote}`);
        } catch (err) {
            showError(err?.response?.data?.error || t('zonesAdmin.saveError', 'Erreur lors de l\'enregistrement'));
        } finally {
            setSaving(false);
        }
    };

    const cancel = () => {
        setRows(toRows(saved));
        setSearch('');
    };

    const resetToDefault = async () => {
        if (!window.confirm(
            t('zonesAdmin.confirmReset', 'Revenir à la liste de zones par défaut ?\n\nLa liste personnalisée de ce serveur sera effacée.')
        )) return;
        setSaving(true);
        try {
            const res = await api.delete(`/zones/${serverId}`);
            applyResponse(res.data);
            success(t('zonesAdmin.resetDone', 'Liste par défaut rétablie'));
        } catch (err) {
            showError(err?.response?.data?.error || t('zonesAdmin.resetError', 'Erreur lors de la réinitialisation'));
        } finally {
            setSaving(false);
        }
    };

    // ── Copie depuis un autre serveur ──────────────────────────────────
    // Les zones et leur ordre viennent de la source ; le statut BDA reste celui de ce serveur
    const copySources = [...(user?.adminGuilds || []), ...(user?.memberGuilds || [])]
        .filter((g, i, all) => g.id !== serverId && all.findIndex(x => x.id === g.id) === i && can(g.id, 'zones'));
    const [copySourceId, setCopySourceId] = useState('');
    const [copying, setCopying] = useState(false);

    const copyFromServer = async () => {
        const source = copySources.find(g => g.id === copySourceId);
        if (!source) return;
        if (isDirty && !window.confirm(t('zonesAdmin.copyDiscard', 'Vos modifications non enregistrées seront remplacées. Continuer ?'))) return;
        setCopying(true);
        try {
            const res = await api.get(`/zones/${copySourceId}`);
            const sourceZones = Array.isArray(res.data?.zones) ? res.data.zones : [];
            if (sourceZones.length === 0) {
                showError(t('zonesAdmin.copyEmpty', 'Ce serveur n\'a aucune zone'));
                return;
            }

            setRows(copyZoneList(toRows(saved), sourceZones));
            setSearch('');
            setView('all');
            success(t('zonesAdmin.copyDone', 'Liste copiée depuis {server} : vérifiez puis enregistrez', { server: source.name }));
        } catch (err) {
            showError(err?.response?.data?.error || t('zonesAdmin.copyError', 'Impossible de charger les zones de ce serveur'));
        } finally {
            setCopying(false);
        }
    };

    const query = normalize(search);
    const bdaCount = rows.filter(r => r.bda).length;
    const visibleRows = rows
        .filter(r => view === 'all' || (view === 'bda' ? r.bda : !r.bda))
        .filter(r => !query || normalize(r.zone).includes(query) || normalize(r.donjon).includes(query));

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="map"
                title={t('navigation.zones', 'Config des zones')}
                subtitle={t('zonesAdmin.subtitle', 'Zones proposées aux membres pour les réservations')}
                actions={
                    <>
                        {isDirty && (
                            <button type="button" className="c2-btn c2-btn--ghost" onClick={cancel} disabled={saving}>
                                <Icon name="close" size={18} />
                                {t('common.cancel', 'Annuler')}
                            </button>
                        )}
                        <button type="button" className="c2-btn c2-btn--primary" onClick={save} disabled={!canSave}>
                            <Icon name="save" size={18} />
                            {saving ? t('zonesAdmin.saving', 'Enregistrement...') : t('zonesAdmin.save', 'Enregistrer')}
                        </button>
                    </>
                }
            />

            {!loading && (
                <div className="c2-kpis">
                    <div className="c2-kpi">
                        <span className="c2-kpi__value">{rows.length}</span>
                        <span className="c2-kpi__label">{t('zonesAdmin.zones', 'Zones')}</span>
                    </div>
                    <div className="c2-kpi">
                        <span className="c2-kpi__value zones-bda-text">{bdaCount}</span>
                        <span className="c2-kpi__label">{t('zonesAdmin.bdaZones', 'Zones BDA (privatisées)')}</span>
                    </div>
                    <div className="c2-kpi">
                        <span className="c2-kpi__value zones-kpi-text">{custom ? t('zonesAdmin.custom', 'Personnalisée') : t('zonesAdmin.default', 'Par défaut')}</span>
                        <span className="c2-kpi__label">
                            {custom && updatedAt ? t('zonesAdmin.modifiedOn', 'Liste modifiée le {date}', { date: new Date(updatedAt).toLocaleDateString(getLocale()) }) : t('zonesAdmin.serverList', 'Liste du serveur')}
                        </span>
                    </div>
                </div>
            )}

            {/* ── Ajout ── */}
            <section className="c2-card c2-section">
                <h2 className="c2-section__title"><Icon name="plus" size={20} />{t('zonesAdmin.addTitle', 'Ajouter une zone')}</h2>
                <p className="c2-section__desc">
                    {t('zonesAdmin.addDesc', 'Les nouvelles zones s\'ajoutent en fin de liste et en fin de classement pour les membres qui ont déjà rempli leurs préférences.')}
                </p>
                <form className="c2-row" onSubmit={addZone}>
                    <label className="c2-field zones-add__field">
                        <span className="c2-field__label">{t('zonesAdmin.zone', 'Zone')}</span>
                        <input
                            className="c2-input"
                            value={newZone.zone}
                            maxLength={120}
                            placeholder={t('zonesAdmin.zonePlaceholder', 'Ex : NILEZA')}
                            onChange={e => setNewZone(prev => ({ ...prev, zone: e.target.value }))}
                        />
                    </label>
                    <label className="c2-field zones-add__field">
                        <span className="c2-field__label">{t('zonesAdmin.dungeonOptional', 'Donjon (facultatif)')}</span>
                        <input
                            className="c2-input"
                            value={newZone.donjon}
                            maxLength={120}
                            placeholder={t('zonesAdmin.dungeonPlaceholder', 'Ex : Jardin d\'Hivers')}
                            onChange={e => setNewZone(prev => ({ ...prev, donjon: e.target.value }))}
                        />
                    </label>
                    <button type="submit" className="c2-btn c2-btn--accent" disabled={!newZone.zone.trim()}>
                        <Icon name="plus" size={18} />
                        {t('common.add', 'Ajouter')}
                    </button>
                </form>
            </section>

            {/* ── Liste ── */}
            <section className="c2-card c2-section">
                <div className="c2-section__head">
                    <h2 className="c2-section__title"><Icon name="map" size={20} />{t('zonesAdmin.listTitle', 'Liste des zones')}</h2>
                    <div className="zones-tools">
                    <div className="zones-views" role="tablist" aria-label={t('zonesAdmin.filterZones', 'Filtrer les zones')}>
                        {[['all', t('zonesAdmin.viewAll', 'Toutes'), rows.length], ['open', t('zonesAdmin.viewOpen', 'Ouvertes'), rows.length - bdaCount], ['bda', 'BDA', bdaCount]].map(([id, label, n]) => (
                            <button
                                key={id}
                                type="button"
                                role="tab"
                                aria-selected={view === id}
                                className={`zones-view${view === id ? ' is-active' : ''}`}
                                onClick={() => setView(id)}
                            >
                                {label} <span className="zones-view__count">{n}</span>
                            </button>
                        ))}
                    </div>
                    <input
                        className="c2-input zones-search"
                        type="search"
                        placeholder={t('zonesAdmin.searchPlaceholder', 'Rechercher une zone ou un donjon...')}
                        aria-label={t('zonesAdmin.searchLabel', 'Rechercher une zone')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    </div>
                </div>
                <p className="c2-section__desc">
                    {t('zonesAdmin.listDesc', 'Une zone BDA est privatisée par le serveur : elle disparaît des préférences des membres et n\'est plus attribuée dans les fiches de réservation (sauf fiches figées). L\'ordre sert de classement par défaut pour les membres qui n\'ont pas rempli leurs préférences. Renommer une zone met à jour les préférences des membres ; les fiches déjà enregistrées gardent l\'ancien nom.')}
                </p>

                {(isDirty || duplicates.size > 0 || emptyCount > 0) && (
                    <div className="zones-status">
                        {isDirty && <span className="c2-chip c2-chip--purple">{t('zonesAdmin.unsaved', 'Modifications non enregistrées')}</span>}
                        {removedCount > 0 && <span className="c2-chip">{t('zonesAdmin.removedCount', '{n} zone(s) supprimée(s)', { n: removedCount })}</span>}
                        {duplicates.size > 0 && <span className="c2-chip zones-chip--error">{t('zonesAdmin.duplicates', 'Noms en double')}</span>}
                        {emptyCount > 0 && <span className="c2-chip zones-chip--error">{t('zonesAdmin.unnamed', 'Zone sans nom')}</span>}
                    </div>
                )}

                {loading ? (
                    <div className="c2-loading">
                        <div className="c2-spinner" />
                        <p>{t('common.loading', 'Chargement...')}</p>
                    </div>
                ) : visibleRows.length === 0 ? (
                    <div className="c2-empty">
                        <Icon name="map" size={36} />
                        <p>{search ? t('zonesAdmin.noMatch', 'Aucune zone ne correspond à la recherche') : t('zonesAdmin.noZone', 'Aucune zone')}</p>
                    </div>
                ) : (
                    <div className="c2-table-wrap">
                        <table className="c2-table zones-table">
                            <thead>
                                <tr>
                                    <th className="is-center">#</th>
                                    <th>{t('zonesAdmin.zone', 'Zone')}</th>
                                    <th>{t('zonesAdmin.dungeon', 'Donjon')}</th>
                                    <th className="is-center">BDA</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.map(r => {
                                    const idx = rows.indexOf(r);
                                    const isDuplicate = duplicates.has(normalize(r.zone));
                                    const isNew = r.originalZone === null;
                                    return (
                                        <tr key={r.key} className={[isNew && 'zones-row--new', r.bda && 'zones-row--bda'].filter(Boolean).join(' ') || undefined}>
                                            <td className="is-center c2-muted">{idx + 1}</td>
                                            <td>
                                                <input
                                                    className={`c2-input c2-input--sm zones-input${isDuplicate || !r.zone.trim() ? ' is-invalid' : ''}`}
                                                    value={r.zone}
                                                    maxLength={120}
                                                    aria-label={t('zonesAdmin.zoneName', 'Nom de la zone')}
                                                    onChange={e => updateRow(r.key, 'zone', e.target.value)}
                                                />
                                                {!isNew && r.zone.trim() !== r.originalZone && (
                                                    <span className="zones-was">{t('zonesAdmin.formerly', 'anciennement « {name} »', { name: r.originalZone })}</span>
                                                )}
                                            </td>
                                            <td>
                                                <input
                                                    className="c2-input c2-input--sm zones-input"
                                                    value={r.donjon}
                                                    maxLength={120}
                                                    placeholder="—"
                                                    aria-label={t('zonesAdmin.dungeon', 'Donjon')}
                                                    onChange={e => updateRow(r.key, 'donjon', e.target.value)}
                                                />
                                            </td>
                                            <td className="is-center">
                                                <button
                                                    type="button"
                                                    className={`zones-bda${r.bda ? ' is-on' : ''}`}
                                                    aria-pressed={r.bda}
                                                    onClick={() => updateRow(r.key, 'bda', !r.bda)}
                                                    title={r.bda ? t('zonesAdmin.reopen', 'Rouvrir la zone aux membres') : t('zonesAdmin.privatize', 'Privatiser la zone (BDA)')}
                                                >
                                                    {r.bda ? 'BDA' : t('zonesAdmin.open', 'Ouverte')}
                                                </button>
                                            </td>
                                            <td className="is-num zones-actions">
                                                <button
                                                    type="button"
                                                    className="c2-icon-btn c2-icon-btn--sm"
                                                    onClick={() => moveRow(r.key, -1)}
                                                    disabled={idx === 0}
                                                    aria-label={t('zonesAdmin.moveUp', 'Monter')}
                                                    title={t('zonesAdmin.moveUp', 'Monter')}
                                                >
                                                    <Icon name="chevronUp" size={16} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="c2-icon-btn c2-icon-btn--sm"
                                                    onClick={() => moveRow(r.key, 1)}
                                                    disabled={idx === rows.length - 1}
                                                    aria-label={t('zonesAdmin.moveDown', 'Descendre')}
                                                    title={t('zonesAdmin.moveDown', 'Descendre')}
                                                >
                                                    <Icon name="chevronDown" size={16} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger"
                                                    onClick={() => removeRow(r.key)}
                                                    aria-label={t('zonesAdmin.deleteZone', 'Supprimer {zone}', { zone: r.zone })}
                                                    title={t('common.delete', 'Supprimer')}
                                                >
                                                    <Icon name="trash" size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {copySources.length > 0 && (
                <section className="c2-card c2-section">
                    <h2 className="c2-section__title"><Icon name="copy" size={20} />{t('zonesAdmin.copyTitle', 'Copier depuis un autre serveur')}</h2>
                    <p className="c2-section__desc">
                        {t('zonesAdmin.copyDesc', 'Reprend la liste et l\'ordre des zones d\'un autre serveur. Les zones BDA de ce serveur restent BDA (et sont gardées même si l\'autre serveur ne les a pas). Rien n\'est enregistré avant de cliquer sur « Enregistrer ».')}
                    </p>
                    <div className="c2-row">
                        <select
                            className="c2-select zones-copy__select"
                            value={copySourceId}
                            onChange={e => setCopySourceId(e.target.value)}
                            aria-label={t('zonesAdmin.copySource', 'Serveur source')}
                        >
                            <option value="">{t('zonesAdmin.copyPick', '— Choisir un serveur —')}</option>
                            {copySources.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                        </select>
                        <button type="button" className="c2-btn c2-btn--accent" onClick={copyFromServer} disabled={!copySourceId || copying || saving || loading}>
                            <Icon name="copy" size={18} />
                            {copying ? t('common.loading', 'Chargement...') : t('zonesAdmin.copyBtn', 'Copier la liste')}
                        </button>
                    </div>
                </section>
            )}

            {custom && (
                <section className="c2-card c2-section">
                    <h2 className="c2-section__title"><Icon name="history" size={20} />{t('zonesAdmin.defaultList', 'Liste par défaut')}</h2>
                    <p className="c2-section__desc">
                        {t('zonesAdmin.defaultListDesc', 'Efface la liste personnalisée de ce serveur et revient à la liste de zones fournie avec le site.')}
                    </p>
                    <div className="c2-row">
                        <button type="button" className="c2-btn c2-btn--danger" onClick={resetToDefault} disabled={saving}>
                            <Icon name="refresh" size={18} />
                            {t('zonesAdmin.restoreDefault', 'Rétablir la liste par défaut')}
                        </button>
                    </div>
                </section>
            )}
        </div>
    );
};

export default ZonesPage;
